'use client';

import { Box, Hide, HStack, Show, SimpleGrid } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { NestedProject } from '@habity/types';
import { getAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { getQueryParamsString } from '~/lib/providers/neondatabase';
import { STYLES } from '~/components/utility/utils';
import { ProjectListCard } from '~/components/projects/list/ProjectListCard';

interface CuratedProjectsGridProps {
  projectIds: string; // Comma-separated project ids
  desktopLayout?: 'grid' | 'scroll';
}

export function CuratedProjectsGrid({
  projectIds,
  desktopLayout = 'grid',
}: CuratedProjectsGridProps) {
  const [projects, setProjects] = useState<NestedProject[]>([]);

  const fetchProjects = async () => {
    const queryParams = getQueryParamsString({
      page: '0',
      limit: projectIds.split(',').length,
      searchParams: new URLSearchParams(),
      entityIds: projectIds,
    });
    const _projects: NestedProject[] = await getAPI(
      API_PATHS.PROVIDERS.NEONDATABASE.PROJECTS,
      queryParams
    );

    setProjects(_projects);
  };

  useEffect(() => {
    fetchProjects();
  }, [projectIds]);

  return (
    <>
      {/* Desktop layout is chosen from props */}
      <Hide below="desktop">
        {desktopLayout === 'grid' ? (
          <SimpleGrid
            w="full"
            minChildWidth="380px"
            rowGap="10"
            columnGap="5"
            pb={{ mobile: '12', desktop: '24' }}
            pt="0"
          >
            {projects.map((project: NestedProject) => (
              <ProjectListCard project={project} />
            ))}
          </SimpleGrid>
        ) : (
          <ProjectsListSideScroll projects={projects} />
        )}
      </Hide>

      {/* Mobile layout is always side-scroll */}
      <Show below="desktop">
        <ProjectsListSideScroll projects={projects} />
      </Show>
    </>
  );
}

interface ProjectsListSideScrollProps {
  projects: NestedProject[];
}

function ProjectsListSideScroll({ projects }: ProjectsListSideScrollProps) {
  return (
    <HStack
      w="full"
      overflowX="scroll"
      sx={{
        ...STYLES.hideScrollbar,
      }}
    >
      {projects.map((project: NestedProject) => (
        <Box w="400px" flexShrink="0">
          <ProjectListCard
            project={project}
            previewImages={1}
          />
        </Box>
      ))}
    </HStack>
  );
}
